import { useCallback, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useLoginModal } from '@/contexts/LoginModalContext';
import { PaidPlanGate } from '@/components/PaidPlanGate';
import { useInventory } from '@/hooks/useInventory';
import { useInventoryActions } from '@/hooks/useInventoryActions';
import { useInventoryLedger } from '@/hooks/useInventoryLedger';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { InventoryKpiCards } from '@/components/inventory/InventoryKpiCards';
import { InventoryTable } from '@/components/inventory/InventoryTable';
import { UnmatchedItemsPanel } from '@/components/inventory/UnmatchedItemsPanel';
import { LedgerFilterBar } from '@/components/inventory/LedgerFilterBar';
import { LedgerList } from '@/components/inventory/LedgerList';
import { InventoryFaq } from '@/components/inventory/InventoryFaq';
import { EditItemModalContainer, type EditItemModalHandle } from '@/components/inventory/EditItemModalContainer';
import { NewItemModalContainer, type NewItemModalHandle } from '@/components/inventory/NewItemModalContainer';
import { MovementModalContainer, type MovementModalHandle } from '@/components/inventory/MovementModalContainer';
import { DeleteItemDialogContainer, type DeleteItemDialogHandle } from '@/components/inventory/DeleteItemDialogContainer';
import {
  BulkDeleteItemsDialogContainer,
  type BulkDeleteItemsDialogHandle,
} from '@/components/inventory/BulkDeleteItemsDialogContainer';
import { ExcelImportModalContainer, type ExcelImportModalHandle } from '@/components/inventory/ExcelImportModalContainer';
import {
  RegisterUnmatchedItemDialogContainer,
  type RegisterUnmatchedItemDialogHandle,
} from '@/components/inventory/RegisterUnmatchedItemDialogContainer';
import { EMPTY_LEDGER_FILTERS, hasActiveLedgerFilters, isLowStock } from '@/supabase/inventory/types/inventory';
import type { InventoryItem, InventoryOverviewItem, LedgerFilters, UnmatchedOrderItem } from '@/supabase/inventory/types/inventory';

type InventoryTab = 'list' | 'ledger';

interface InventoryContentProps {
  // 컨테이너 ref는 Inventory(부모)가 소유해 주입한다 — 헤더의 '신규 상품 등록' 버튼이
  // 게스트/로그인 두 분기를 하나의 핸들러로 처리하려면 ref가 부모에도 있어야 한다
  // (CombinedShippingModalContainer 선례 — 훅/컨테이너 자체 ref 생성 금지).
  newItemModalRef: RefObject<NewItemModalHandle>;
}

/**
 * 데이터 훅(useInventory/useInventoryActions/useInventoryLedger)을 소유하는 서브트리.
 * Inventory()가 로그인 사용자에게만 이 컴포넌트를 마운트해 게스트에게는 재고 fetch가
 * 전혀 나가지 않도록 한다(useInventory는 mount 시 무조건 fetch하는 훅이라 렌더 조건부로는
 * 막을 수 없고, 컴포넌트 자체를 마운트하지 않아야 한다).
 */
function InventoryContent({ newItemModalRef }: InventoryContentProps) {
  const {
    items,
    isLoading: isItemsLoading,
    hasError: hasItemsError,
    refresh,
    windowDays,
    scanTruncated,
    unmatched,
    unmatchedTotal,
  } = useInventory();
  const {
    entries,
    isLoading: isLedgerLoading,
    hasMore,
    loadMore,
    reload: reloadLedger,
    hasError: hasLedgerError,
    filters: ledgerFilters,
    applyFilters: applyLedgerFilters,
  } = useInventoryLedger();
  // KPI는 2타일(등록 상품/부족) 전부 items 파생(왕복 0) — 오늘 입고/출고 서버 집계
  // 타일은 오너 결정으로 제거(2026-07-16 개념 다이어트: 원장 유형 구분을 셀러에게 설명해야
  // 이해되는 숫자라 뺌). useInventoryTodayKpis 훅·RPC는 남아 있어 복원 가능.
  // 부족·품절 카운트 — 판정은 isLowStock 단일 정의(InventoryTable '부족만 보기'와 동일 판정
  // 공유, PR7-1 F7 교훈: 판정 중복 금지).
  const lowStockCount = useMemo(() => items.filter(isLowStock).length, [items]);
  // 원장 탭을 한 번이라도 로드했는지 — state가 아니라 ref인 이유: 이 값을 읽는 곳은
  // onMutated/handleTabChange 클로저뿐이라 리렌더를 트리거할 필요가 없다.
  const hasLoadedLedgerRef = useRef(false);

  const [tab, setTab] = useState<InventoryTab>('list');
  // onMutated는 [refresh, reloadLedger] deps로 고정된 안정 콜백이라(CLAUDE.md 규율 5, memo 자식
  // useInventoryActions에 주입) tab을 deps에 넣어 매 탭 전환마다 정체성이 바뀌게 할 수 없다.
  // CombinedShippingModalContainer의 latest-ref 패턴을 그대로 써서, deps 없는 useLayoutEffect로
  // commit마다 최신 tab을 ref에 동기화해 onMutated가 stale closure 없이 최신 탭을 읽게 한다.
  const tabRef = useRef(tab);
  useLayoutEffect(() => {
    tabRef.current = tab;
  });

  // reload 성공 시에만 "최초 로드 완료" 부기를 세우는 공통 로직 — handleTabChange(최초 진입)와
  // onMutated(뮤테이션 후 재조회)와 재시도(Fix D onRetry) 셋이 동일한 규칙을 쓰므로 훅 하나로 추출.
  const runLedgerReload = useCallback(async () => {
    const success = await reloadLedger();
    if (success) hasLoadedLedgerRef.current = true;
    return success;
  }, [reloadLedger]);

  // 필터 변경도 runLedgerReload와 동일한 부기 규칙(applied 성공 시에만 hasLoadedLedgerRef) —
  // 필터 조작은 ledger 탭 진입 후에만 가능해 사실상 항상 로드 후지만, "ref는 실제 반영된
  // 로드에만 세운다" 계약을 경로마다 다른 규칙으로 쪼개지 않는다(계약 일관성).
  const handleLedgerFiltersChange = useCallback(async (next: LedgerFilters) => {
    const success = await applyLedgerFilters(next);
    if (success) hasLoadedLedgerRef.current = true;
  }, [applyLedgerFilters]);

  const handleResetLedgerFilters = useCallback(() => {
    void handleLedgerFiltersChange(EMPTY_LEDGER_FILTERS);
  }, [handleLedgerFiltersChange]);

  // 상품 필터가 걸린 상품이 활성 목록(items)에서 빠진 경우(소프트 삭제)의 라벨 폴백 — 현재
  // 로드된 원장 행의 item_name 스냅샷에서 유도한다(service가 삭제 상품 이름도 머지해 줌).
  // 필터 결과 0건이면 null → 필터 바가 '선택한 상품' 대체 문구로 처리(codexloop PR7 R1 P2).
  const ledgerFilterItemFallbackName = useMemo(() => {
    if (ledgerFilters.itemId === null) return null;
    return entries.find((entry) => entry.item_id === ledgerFilters.itemId)?.item_name ?? null;
  }, [entries, ledgerFilters.itemId]);

  const onMutated = useCallback(async () => {
    await refresh();
    // 원장을 한 번이라도 로드했으면 항상 재조회하고, 아직 로드 전이라도 지금 원장 탭이
    // 활성 상태(예: 스켈레톤 로딩 중 다른 경로로 뮤테이션 발생)라면 함께 최신화한다
    // (codexloop R1 Fix B) — tabRef는 위 latest-ref라 이 분기가 최신 탭을 stale 없이 본다.
    if (hasLoadedLedgerRef.current || tabRef.current === 'ledger') {
      await runLedgerReload();
    }
  }, [refresh, runLedgerReload]);

  const { createItem, updateItem, recordMovement, softDeleteItem, softDeleteItems, bulkUpsert, isSubmitting } = useInventoryActions({ onMutated });

  // 미매칭 패널의 행별 등록 pending 상태(콜로케이션 — 이 상태를 읽는 유일한 소비처가
  // UnmatchedItemsPanel). ref는 handleRegisterUnmatchedItem 내부의 동기 더블클릭 가드용,
  // state는 렌더(버튼 disabled/문구)용 — useInventoryActions의 isSubmittingRef+isSubmitting
  // 쌍과 동일한 이유(state만으로는 연속 클릭 사이 리렌더 지연 구간에 가드가 안 걸림).
  const registeringNormsRef = useRef<Set<string>>(new Set());
  const [registeringNorms, setRegisteringNorms] = useState<Set<string>>(new Set());

  // 다이얼로그 제출 경로(오너 피드백 2026-07-13: 등록 전에 현재고를 물어본다). 성공 여부를
  // 반환해 다이얼로그가 닫힘을 결정한다. pending 가드·실패 재동기화 로직은 원클릭 시절
  // 핸들러 그대로 — 제출 중 패널 행 '등록 중' 표시와 동기 더블서밋 가드가 함께 유지된다.
  const handleRegisterUnmatchedItem = useCallback(
    async (item: UnmatchedOrderItem, initialStock: number, lowStockThreshold: number): Promise<boolean> => {
      if (registeringNormsRef.current.has(item.normName)) return false;
      const pending = new Set(registeringNormsRef.current);
      pending.add(item.normName);
      registeringNormsRef.current = pending;
      setRegisteringNorms(pending);
      try {
        // initialStock은 다이얼로그에서 셀러가 입력한 현재고(모르면 디폴트 0 — 소급 차감
        // 없음 원칙은 그대로, 0으로 시작해 '조정'으로 맞추는 흐름). name은 주문 원문 그대로,
        // price는 unmatched 스냅샷의 대표 가격. 성공 시 createItem이 토스트 + onMutated
        // (refresh)를 이미 수행하므로 여기서는 pending 해제만 하면 된다 — 등록된 항목은
        // refresh로 갱신되는 unmatched 목록에서 자연히 사라진다(서버가 소프트 삭제 마스터도
        // 복원해 재등록 처리).
        // 대표 가격은 주문 라인 그대로라 음수일 수 있는데(할인 라인 등 — 주문 편집은 음수
        // 허용) create RPC는 음수를 거부(P0011)해 등록이 매번 실패하고 행이 남는다 — 0으로
        // 클램프해 등록은 통과시키고 가격은 셀러가 목록에서 수정(마스터 price는 편집 가능한
        // 기본값).
        const created = await createItem({
          name: item.name,
          initialStock,
          price: Math.max(0, item.price),
          // 다이얼로그에서 셀러가 입력한 부족 알림 기준(디폴트 소유권은 다이얼로그 —
          // DEFAULT_THRESHOLD '5', 이전 페이지 하드코딩 상수는 제거됨).
          lowStockThreshold,
        });
        // 실패 시(createItem은 throw 없이 false 반환·에러 토스트는 내부 처리) onMutated가 안
        // 불려 stale 행이 남는다 — 대표 케이스: 다른 탭/기기에서 이미 등록된 norm의 duplicate.
        // refresh로 서버 기준 재동기화하면 이미 등록된 행은 자연 소멸한다(codexloop PR3 R1 #4 수용).
        if (!created) await refresh();
        return created;
      } finally {
        const settled = new Set(registeringNormsRef.current);
        settled.delete(item.normName);
        registeringNormsRef.current = settled;
        setRegisteringNorms(settled);
      }
    },
    [createItem, refresh],
  );

  const movementModalRef = useRef<MovementModalHandle>(null);
  const editItemModalRef = useRef<EditItemModalHandle>(null);
  const deleteDialogRef = useRef<DeleteItemDialogHandle>(null);
  const bulkDeleteDialogRef = useRef<BulkDeleteItemsDialogHandle>(null);
  const excelImportModalRef = useRef<ExcelImportModalHandle>(null);
  const registerUnmatchedDialogRef = useRef<RegisterUnmatchedItemDialogHandle>(null);

  // 패널의 '재고로 등록' 클릭 = 다이얼로그 오픈(즉시 등록 아님 — 현재고 입력 단계 삽입).
  const handleOpenRegisterUnmatched = useCallback((item: UnmatchedOrderItem) => {
    registerUnmatchedDialogRef.current?.open(item);
  }, []);

  const handleTabChange = useCallback(async (value: string) => {
    if (value !== 'list' && value !== 'ledger') return;
    setTab(value);
    // 내역 탭 최초 진입 시에만 lazy reload — 이후 탭 왕복은 재로드하지 않는다. ref는 성공
    // 시에만 세운다(PR2 최종리뷰 Fix 2) — 최초 로드가 실패하면 ref가 여전히 false로 남아
    // 다음 탭 재진입에서 재시도된다(안 그러면 첫 실패 후 영원히 재로드 안 되는 함정).
    if (value === 'ledger' && !hasLoadedLedgerRef.current) {
      await runLedgerReload();
    }
  }, [runLedgerReload]);

  // PR3 리뷰 수용: MovementModalHandle.open이 이제 InventoryOverviewItem을 받는다(모달 배지가
  // 가용 판정을 하려면 reserved가 필요) — 실제로 InventoryTable/InventoryRow가 넘기는 런타임
  // 값은 항상 useInventory의 overview item이라 이 타입 확장은 동작 변경 없는 정직한 확장이다.
  const handleOpenMovement = useCallback((item: InventoryOverviewItem) => {
    movementModalRef.current?.open(item, 'movement');
  }, []);
  const handleOpenAdjust = useCallback((item: InventoryOverviewItem) => {
    movementModalRef.current?.open(item, 'adjust');
  }, []);
  const handleOpenEdit = useCallback((item: InventoryItem) => {
    editItemModalRef.current?.open(item);
  }, []);
  const handleDelete = useCallback((item: InventoryItem) => {
    deleteDialogRef.current?.open(item);
  }, []);
  const handleBulkDelete = useCallback((items: InventoryItem[]) => {
    bulkDeleteDialogRef.current?.open(items);
  }, []);
  const handleOpenExcelImport = useCallback(() => {
    excelImportModalRef.current?.open();
  }, []);

  return (
    <>
      <InventoryKpiCards
        totalCount={items.length}
        lowStockCount={lowStockCount}
        isItemsLoading={isItemsLoading}
        hasItemsError={hasItemsError}
      />
      <Tabs value={tab} onValueChange={handleTabChange}>
        {/* 이 페이지는 slate 단일 팔레트(카드 ring/텍스트 전부 slate) — TabsList 기본값만
            gray 계열이라 사용처에서 slate로 통일(전역 tabs.tsx는 Home 등 타 페이지가 써서
            여기서만 오버라이드). */}
        <TabsList className="bg-slate-100 text-slate-500">
          <TabsTrigger value="list">재고 목록</TabsTrigger>
          <TabsTrigger value="ledger">입출고 내역</TabsTrigger>
        </TabsList>
        <TabsContent value="list" className="space-y-4 pt-4">
          <UnmatchedItemsPanel
            unmatched={unmatched}
            unmatchedTotal={unmatchedTotal}
            onRegister={handleOpenRegisterUnmatched}
            registeringNorms={registeringNorms}
          />
          <InventoryTable
            items={items}
            isLoading={isItemsLoading}
            hasError={hasItemsError}
            onRetry={refresh}
            headerAction={
              /* UI 폴리시: 버튼 하나짜리 고아 행을 없애고 컨트롤 행(검색·부족만 보기)에 병합.
                 disabled 조건은 기존 그대로 — items 로드 실패/초기 로딩 중엔 열지 못하게
                 (codexloop PR5 R1 Fix 1: replace 삭제 예고 개수가 items 기준이라, 빈 상태에서
                 "0개 삭제"로 오예고 후 대량 소프트삭제될 수 있음). */
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenExcelImport}
                disabled={hasItemsError || (isItemsLoading && items.length === 0)}
              >
                엑셀 일괄등록
              </Button>
            }
            onOpenMovement={handleOpenMovement}
            onOpenAdjust={handleOpenAdjust}
            onOpenEdit={handleOpenEdit}
            onDelete={handleDelete}
            onBulkDelete={handleBulkDelete}
            windowDays={windowDays}
            scanTruncated={scanTruncated}
          />
        </TabsContent>
        <TabsContent value="ledger" className="space-y-4 pt-4">
          <LedgerFilterBar
            items={items}
            isItemsLoading={isItemsLoading}
            hasItemsError={hasItemsError}
            selectedItemFallbackName={ledgerFilterItemFallbackName}
            filters={ledgerFilters}
            onChange={handleLedgerFiltersChange}
          />
          <LedgerList
            entries={entries}
            isLoading={isLedgerLoading}
            hasMore={hasMore}
            onLoadMore={loadMore}
            hasError={hasLedgerError}
            onRetry={runLedgerReload}
            isFiltered={hasActiveLedgerFilters(ledgerFilters)}
            onResetFilters={handleResetLedgerFilters}
          />
        </TabsContent>
      </Tabs>

      {/* FAQ는 탭 밖(양 탭 공통) 페이지 하단 — 개념 질문(예약·가용·차감)은 탭 무관.
          fragment라 부모 space-y-6이 그대로 간격을 준다. */}
      <InventoryFaq />

      <NewItemModalContainer ref={newItemModalRef} createItem={createItem} isSubmitting={isSubmitting} />
      <EditItemModalContainer ref={editItemModalRef} updateItem={updateItem} isSubmitting={isSubmitting} />
      <MovementModalContainer ref={movementModalRef} recordMovement={recordMovement} isSubmitting={isSubmitting} />
      <DeleteItemDialogContainer ref={deleteDialogRef} softDeleteItem={softDeleteItem} isSubmitting={isSubmitting} />
      <BulkDeleteItemsDialogContainer
        ref={bulkDeleteDialogRef}
        softDeleteItems={softDeleteItems}
        isSubmitting={isSubmitting}
      />
      <RegisterUnmatchedItemDialogContainer
        ref={registerUnmatchedDialogRef}
        onSubmit={handleRegisterUnmatchedItem}
        isSubmitting={isSubmitting}
      />
      <ExcelImportModalContainer
        ref={excelImportModalRef}
        bulkUpsert={bulkUpsert}
        isSubmitting={isSubmitting}
        activeItems={items}
      />
    </>
  );
}

/**
 * 게스트 가드: Deposits.tsx는 페이지 전체를 조건부로 감싸지 않고 훅 내부(useDeposits가
 * userId를 보고 자체 판단)에서 fetch를 건너뛴다. useInventory는 그런 인자가 없는 mount-fetch
 * 훅(Task 1~4 기 배선, 이 태스크에서 수정 불가)이라 동일한 방식을 그대로 복제할 수 없다 —
 * 대신 Deposits와 같은 "페이지는 항상 같은 틀을 보여주고, 게스트의 액션 버튼은 로그인
 * 모달을 연다"는 행동을 유지하면서, 데이터를 쥔 서브트리(InventoryContent)만 로그인
 * 사용자에게만 마운트해 fetch 자체가 나가지 않게 한다(Dashboard.tsx의 페이지 레벨 가드를
 * 이 훅 제약에 맞게 최소 변형 — 새 가드 패턴을 발명한 것이 아니라 기존 두 선례의 조합).
 */
function InventoryPage() {
  const { user, loading: authLoading } = useAuth();
  const { openLoginModal } = useLoginModal();
  const newItemModalRef = useRef<NewItemModalHandle>(null);

  const handleNewItemClick = useCallback(() => {
    if (!user) {
      openLoginModal();
      return;
    }
    newItemModalRef.current?.open();
  }, [user, openLoginModal]);

  if (authLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <Loader2 className="h-16 w-16 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 text-slate-900 md:p-6 lg:p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {/* 부제 = 가치 제안 한 줄(오너 확정 2026-07-17 A안). 카피 개편(07-16) 때 정보 없는
              옛 부제("등록하고 기록해 보세요"류)는 삭제했지만, 이 줄은 정산서 연동 자동
              차감이라는 기능의 정체성을 말하므로 승격 복원 — 등록(행동)→입금 완료(시점)→
              자동 차감(혜택) 순서. */}
          <div className="space-y-0.5">
            <h1 className="text-xl font-bold text-slate-900">재고 관리</h1>
            <p className="break-keep text-sm leading-relaxed text-slate-500">
              상품을 등록해 두면 정산서 주문이 입금 완료될 때 재고가 자동 차감됩니다.
            </p>
          </div>
          <Button
            className="min-h-11 shrink-0 bg-slate-900 text-white hover:bg-slate-800"
            onClick={handleNewItemClick}
          >
            신규 상품 등록
          </Button>
        </div>

        {user ? (
          // key=user.id: 계정 A→B 직접 전환(SIGNED_IN, null 경유 없음 — 크로스탭 세션 전파 등)
          // 시 mount-fetch 훅들(items·원장·KPI)이 A의 데이터를 쥔 채 남지 않도록 강제 remount
          // (codexloop PR7-2 R1 P1 수용 — 훅별 가드 대신 페이지 단위 1줄, PR6 교차계정
          // fail-closed 원칙과 동일 취지. user.id는 세션 갱신에 불변이라 불필요 remount 없음).
          <InventoryContent key={user.id} newItemModalRef={newItemModalRef} />
        ) : (
          <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-slate-200">
            <p className="break-keep text-sm font-medium text-slate-700">로그인 후 재고를 관리할 수 있습니다</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Inventory() {
  return (
    <PaidPlanGate message="재고관리는 스탠다드 플랜 이상부터 제공됩니다.">
      <InventoryPage />
    </PaidPlanGate>
  );
}
